import { FormEvent, useMemo, useState } from "react"
import { useModalWindow } from "react-modal-global"
import { useZodFormIssues } from "react-zod-form"

import queryClient from "@/api/client"
import { form, parsePosName, posFieldEntries, posFieldPlaceholders, posInfoText } from "@/constants/posPopupIntegration"
import LocationDao from "@/entities/location/location.dao"
import { QLocation } from "@/entities/location/location.types"
import POSDao from "@/entities/pos/pos.dao"
import { POSIntegrationEditDTO } from "@/entities/pos/pos.dto"
import { POSCBSNorthStarIntegrationConfig, POSIntegration, POSName, POSPageContext } from "@/entities/pos/pos.types"
import PopupLayout from "@/services/Modal/layouts/PopupLayout/PopupLayout"
import Button from "@/shared/components/intrinsic/Button/Button"
import Field from "@/shared/components/intrinsic/Field/Field"
import Icon from "@/shared/components/intrinsic/Icon/Icon"
import Selector from "@/shared/components/intrinsic/Selector/Selector"
import Tooltip from "@/shared/components/intrinsic/Tooltip/Tooltip"
import Fields from "@/shared/layouts/Fields/Fields"
import useFormStatus from "@/utils/hooks/useFormStatus"


interface PopupEditMenuIntegrationProps {
  location: QLocation;
  onSubmit(integration: POSIntegration): Promise<void> | void;
  pageContext?: POSPageContext;
}

function PopupEditMenuIntegration({ location, onSubmit, pageContext }: PopupEditMenuIntegrationProps) {
  const modal = useModalWindow()

  const [posName, setPosName] = useState<POSName | null>(() => parsePosName(location?.pos?.name))
  const [changedFields, setChangedFields] = useState<string[]>([])

  const { fieldIssues } = useZodFormIssues(form)
  const formStatus = useFormStatus(form, handleSubmit)
  const [configData, setConfigData] = useState(JSON.parse(location.pos?.configJson ?? "{}"))
  const formUpdated = useMemo(() => changedFields.length !== 0, [changedFields])

  // Focus-specific state
  const [focusRestaurants, setFocusRestaurants] = useState<{ restaurant_guid: string; restaurant_name: string; created_date: string }[]>([])
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>("")
  const [isFocusAuthenticated, setIsFocusAuthenticated] = useState(false)
  const [focusAuthError, setFocusAuthError] = useState<string>("")
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  function getFilteredPosOptions(): POSName[] {
    const allOptions = Object.values(POSName)

    if (pageContext === "ocbs") {
      return allOptions.filter(pos => pos === POSName.Clover)
    }

    if (pageContext === "menus") {
      return allOptions.filter(pos => pos !== POSName.Clover)
    }

    // Default: show all options (for backward compatibility)
    return allOptions
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    const fields = form.parseAllFields(event)
    if (!fields) return

    if (posName === "CBSNorthStar") {
      fields.clientId = fields.config.site_id as string
    }

    if (posName === "Focus" && selectedRestaurant) {
      fields.config.focus_restaurant = selectedRestaurant
    }

    if (fields.config.displayName) {
      fields.config.displayName = fields.config.displayName.replace(/\s+/g, " ")
    }

    await onSubmit(fields)

    setChangedFields([])
    setConfigData(fields.config)

    if (!location.pos?.name) {
      queryClient.invalidateQueries({ queryKey: [LocationDao.name] })
    }

    modal.close()
  }

  function onChange(event: FormEvent<HTMLFormElement>) {
    const field = form.parseCurrentField(event)
    if (!field) return

    const fieldName = field.name.split(".")[1]

    const isFieldChanged = configData[fieldName] !== field.value
    setChangedFields((fields) =>
      isFieldChanged
        ? fields.includes(fieldName)
          ? fields
          : [...fields, fieldName]
        : fields.filter((changedField) => changedField !== fieldName)
    )
  }

  function parseDefaultValue(fieldName: string) {
    return posName === parsePosName(location?.pos?.name)
      ? fieldName === "client"
        ? location?.pos?.client
        : configData[fieldName]
      : undefined
  }

  async function onFlushUpdates() {
    if (location.pos.configJson == null) {
      throw new TypeError("location.pos.configJson must not be null")
    }

    const config: POSCBSNorthStarIntegrationConfig = JSON.parse(location.pos.configJson)
    const dto: POSIntegrationEditDTO = {
      locationId: location.id,
      name: location.pos.name,
      clientId: location.pos.client,
      config,
    }

    await POSDao.put(dto, "REFRESH")
    modal.close()
  }

  async function handleCloverEndpointCall() {
    try {
      // Redirect to the auth URL in the same tab
      window.location.href = `${import.meta.env.VITE_API_HOST}integration/auth/clover?locationId=${location.id}`
    } catch (error) {
      console.error("Error calling Clover endpoint:", error)
    }
  }

  async function handleFocusAuthentication() {
    const formElement = document.querySelector("#post-integration-form") as HTMLFormElement
    const formData = new FormData(formElement)
    const login = formData.get("config.focus_login") as string
    const password = formData.get("config.focus_password") as string

    if (!login || !password) {
      setFocusAuthError("Please enter both login and password")
      return
    }

    setIsAuthenticating(true)
    setFocusAuthError("")

    try {
      const response = await POSDao.authenticateFocus(login, password)
      setFocusRestaurants(response.items)
      setIsFocusAuthenticated(true)
      setFocusAuthError("")
    } catch (error) {
      console.error("Focus authentication error:", error)
      setFocusAuthError("Authentication failed. Please check your credentials.")
      setIsFocusAuthenticated(false)
      setFocusRestaurants([])
    } finally {
      setIsAuthenticating(false)
    }
  }
  function onCancel() {
    const form = document.querySelector("#post-integration-form") as HTMLFormElement
    if (form) {
      form.reset()
    }

    setPosName(null)
    setConfigData({})
    setChangedFields([])

    // Reset Focus-specific state
    setFocusRestaurants([])
    setSelectedRestaurant("")
    setIsFocusAuthenticated(false)
    setFocusAuthError("")
    setIsAuthenticating(false)

    modal.close()
  }

  function handlePosNameChange(newPosName: POSName | null) {
    setPosName(newPosName)

    // Reset Focus-specific state when changing POS provider
    if (newPosName !== "Focus") {
      setFocusRestaurants([])
      setSelectedRestaurant("")
      setIsFocusAuthenticated(false)
      setFocusAuthError("")
      setIsAuthenticating(false)
    }
  }


  return (
    <PopupLayout title={`${posName ? posName : "Create"} Integration`} location={location} width="60em">
      <form id="post-integration-form" onChange={onChange} onSubmit={formStatus.submit}>
        <Fields>
          <div
            style={{
              display: !location?.pos ? "flex" : "none",
              alignItems: "center",
              gap: "1em"
            }}
          >
            {posName && (
              <Tooltip text={posInfoText[posName]?.posName ?? "Info not available"}>
                <Icon name="info" />
              </Tooltip>
            )}
            <div style={{ flexGrow: 1 }}>
              <Selector
                name={form.fields.name}
                creatable
                placeholder="POS Provider Name"
                defaultValue={posName}
                onCreate={async () => await new Promise((resolve) => setTimeout(resolve, 1000))}
                onChange={handlePosNameChange}
              >
                {getFilteredPosOptions().map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </Selector>
            </div>
          </div>

          {posName != null && (
            <>
              {posName !== "CBSNorthStar" && posName !== "Clover" && (
                <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
                  <Tooltip text={posInfoText[posName]?.clientId ?? "Info not available"}>
                    <Icon name="info" />
                  </Tooltip>
                  <div style={{ flexGrow: 1 }}>
                    <Field
                      name={form.fields.clientId}
                      placeholder="POS Client ID"
                      customValidity={fieldIssues.clientId}
                      defaultValue={parseDefaultValue("client")}
                      key={posName}
                    />
                  </div>
                </div>
              )}

              {posName === "Clover" && (
                <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
                  <Button
                    color="white"
                    await
                    onClick={handleCloverEndpointCall}
                    disabled={!!location?.pos?.name}
                  >
                    {location?.pos?.name ? "Clover Integrated" : "Connect to Clover"}
                  </Button>
                </div>
              )}

              {posName === "Focus" && (
                <>
                  {/* Focus Login Field */}
                  <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
                    <Tooltip text={posInfoText[posName]?.focus_login ?? "Your Focus POS login username"}>
                      <Icon name="info" />
                    </Tooltip>
                    <div style={{ flexGrow: 1 }}>
                      <Field
                        name={form.fields.config.focus_login}
                        placeholder={posFieldPlaceholders.focus_login}
                        customValidity={fieldIssues.config?.focus_login}
                        defaultValue={parseDefaultValue("focus_login")}
                      />
                    </div>
                  </div>

                  {/* Focus Password Field */}
                  <div style={{ display: "flex", alignItems: "center", gap: "1em" }}>
                    <Tooltip text={posInfoText[posName]?.focus_password ?? "Your Focus POS password"}>
                      <Icon name="info" />
                    </Tooltip>
                    <div style={{ flexGrow: 1 }}>
                      <Field
                        name={form.fields.config.focus_password}
                        placeholder={posFieldPlaceholders.focus_password}
                        customValidity={fieldIssues.config?.focus_password}
                        defaultValue={parseDefaultValue("focus_password")}
                        type="password"
                      />
                    </div>
                  </div>

                  {/* Authentication Button */}
                  {!isFocusAuthenticated && (
                    <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
                      <Button
                        color="white"
                        await
                        onClick={handleFocusAuthentication}
                        disabled={isAuthenticating}
                      >
                        {isAuthenticating ? "Authenticating..." : "Authenticate"}
                      </Button>
                    </div>
                  )}

                  {/* Error Message */}
                  {focusAuthError && (
                    <div style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
                      {focusAuthError}
                    </div>
                  )}

                  {/* Restaurant Selector */}
                  {isFocusAuthenticated && focusRestaurants.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "1em", marginTop: "2rem" }}>
                      <Tooltip text="Select the restaurant to integrate with Focus POS">
                        <Icon name="info" />
                      </Tooltip>
                      <div style={{ flexGrow: 1 }}>
                        <Selector
                          placeholder="Select Restaurant"
                          value={selectedRestaurant}
                          onChange={setSelectedRestaurant}
                        >
                          {focusRestaurants.map((restaurant) => (
                            <option key={restaurant.restaurant_guid} value={restaurant.restaurant_guid}>
                              {restaurant.restaurant_name}
                            </option>
                          ))}
                        </Selector>
                      </div>
                    </div>
                  )}
                </>
              )}

              {posFieldEntries.map(
                ([fieldName, posNames]) =>
                  posNames.includes(posName) && posName !== "Clover" && posName !== "Focus" && (
                    <div style={{ display: "flex", alignItems: "center", gap: "1em" }} key={fieldName}>
                      <Tooltip text={posInfoText[posName]?.[fieldName] ?? "Info not available"}>
                        <Icon name="info" />
                      </Tooltip>
                      <div style={{ flexGrow: 1 }}>
                        <Field
                          name={form.fields.config[fieldName]}
                          placeholder={posFieldPlaceholders[fieldName]}
                          customValidity={fieldIssues.config?.[fieldName]}
                          defaultValue={parseDefaultValue(fieldName)}
                        />
                      </div>
                    </div>
                  )
              )}
            </>
          )}

          <div style={{ display: "flex", justifyContent: "center", gap: 46, marginTop: "2.5rem" }}>
            {!location?.pos?.name && <Button color="red" onClick={onCancel}>Cancel</Button>}
            {location?.pos?.name && (
              <Button type="submit" color="white" await onClick={onFlushUpdates}>Refresh Data</Button>
            )}
            {posName !== "Clover" && (
              <Button
                type="submit"
                color="white"
                {...formStatus}
                disabled={
                  formStatus.disabled ||
                  !posName ||
                  !formUpdated ||
                  (posName === "Focus" && (!isFocusAuthenticated || !selectedRestaurant))
                }
              >
                {location?.pos?.name ? "Update" : "Create"}
              </Button>
            )}
          </div>
        </Fields>
      </form>
    </PopupLayout>
  )
}

export default PopupEditMenuIntegration
